import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm, useWatch } from 'react-hook-form';
import { chargeInputSchema, type ChargeCategory, type ChargeInput } from '@pirouette/shared';
import { useCreateCharge, useFamilyAccounts } from '@/data/billing';
import { useFamilies } from '@/data/families';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/billing/new')({
  component: NewCharge,
});

const CATEGORIES: { value: ChargeCategory; label: string }[] = [
  { value: 'tuition', label: 'Tuition' },
  { value: 'costume', label: 'Costume' },
  { value: 'ticket', label: 'Recital ticket' },
  { value: 'competition_entry', label: 'Competition entry' },
  { value: 'late_fee', label: 'Late fee' },
  { value: 'drop_credit', label: 'Drop credit' },
  { value: 'custom', label: 'Custom' },
];

// The form takes dollars (so admins type "125.00", not "12500") and
// converts to cents before persisting. The Zod schema still expects
// integer cents, so the conversion happens in the submit handler.
type NewChargeForm = Omit<ChargeInput, 'amount_cents'> & { amount_dollars: number };

function NewCharge() {
  const navigate = useNavigate();
  const create = useCreateCharge();
  const families = useFamilies({ limit: 200 });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<NewChargeForm>({
    mode: 'onBlur',
    defaultValues: { category: 'tuition', amount_dollars: 0 },
  });

  // Live-watch the family field so the account picker repopulates.
  const watchedFamily = useWatch({ control, name: 'family_id' });
  const accounts = useFamilyAccounts(watchedFamily);

  async function onSubmit(values: NewChargeForm) {
    const { amount_dollars, account_id, ...rest } = values;
    const amount_cents = Math.round(amount_dollars * 100);
    try {
      // Empty-string account_id means "let the trigger pick". Drop the
      // field entirely so the Zod schema's `.optional()` doesn't reject it.
      const payload: ChargeInput & { amount_cents: number } = account_id
        ? { ...rest, account_id, amount_cents }
        : { ...rest, amount_cents };
      const parsed = chargeInputSchema.parse(payload);
      const created = await create.mutateAsync(parsed);
      toast({ title: 'Charge created', variant: 'success' });
      await navigate({
        to: '/admin/billing/$familyId',
        params: { familyId: created.family_id },
      });
    } catch (err) {
      toast({
        title: "Couldn't save",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <div className="max-w-2xl space-y-8">
      <header>
        <Link
          to="/admin/billing"
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Billing
        </Link>
        <h1 className="mt-2">New charge</h1>
      </header>

      <form
        onSubmit={(e) => void handleSubmit(onSubmit)(e)}
        noValidate
        className="surface-elevated grid gap-5 p-6"
      >
        <div>
          <Label htmlFor="ch-family">Family</Label>
          <select
            id="ch-family"
            className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
            aria-invalid={!!errors.family_id}
            defaultValue=""
            {...register('family_id')}
          >
            <option value="" disabled>
              Select a family…
            </option>
            {families.data?.rows.map((f) => (
              <option key={f.id} value={f.id}>
                {f.primary_guardian_name} ({f.billing_email})
              </option>
            ))}
          </select>
          {errors.family_id && (
            <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
              {errors.family_id.message}
            </p>
          )}
        </div>

        <div className="grid gap-5 sm:grid-cols-3">
          <div>
            <Label htmlFor="ch-cat">Category</Label>
            <select
              id="ch-cat"
              className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
              {...register('category')}
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          {/* Sprint 12 — multi-account: admin picks which ledger this
             charge lands in. Leave blank to let the DB trigger pick
             based on category (competition_entry → competition,
             everything else → studio). */}
          <div>
            <Label htmlFor="ch-account">Account</Label>
            <select
              id="ch-account"
              className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
              {...register('account_id')}
              defaultValue=""
            >
              <option value="">Auto (by category)</option>
              {(accounts.data ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} · {a.kind}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="ch-amount">Amount ($)</Label>
            <Input
              id="ch-amount"
              type="number"
              step="0.01"
              aria-invalid={!!errors.amount_dollars}
              {...register('amount_dollars', { valueAsNumber: true })}
            />
            {errors.amount_dollars && (
              <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
                {errors.amount_dollars.message}
              </p>
            )}
          </div>
        </div>

        <div>
          <Label htmlFor="ch-desc">Description</Label>
          <Input id="ch-desc" aria-invalid={!!errors.description} {...register('description')} />
          {errors.description && (
            <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
              {errors.description.message}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="ch-due">Due date</Label>
          <Input id="ch-due" type="date" {...register('due_date')} />
        </div>

        <div>
          <Label htmlFor="ch-notes">Notes</Label>
          <textarea
            id="ch-notes"
            rows={2}
            className="w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3 focus-visible:outline-none"
            {...register('notes')}
          />
        </div>

        <div className="flex gap-3">
          <Button type="submit" disabled={isSubmitting} aria-busy={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Create charge'}
          </Button>
          <Link to="/admin/billing">
            <Button variant="ghost" type="button">
              Cancel
            </Button>
          </Link>
        </div>
      </form>
    </div>
  );
}
